import { request } from './api';
import { getUser } from './user';
import { md5 } from 'super-fast-md5';

export function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function createAssetURL(id) {
  return `asset://${id}`;
}

/** 解析 public 目录下文件的完整 URL */
export function resolvePublicPath(src) {
  if (src.startsWith('/')) src = src.slice(1);
  const base = import.meta.env.BASE_URL || '/';
  const baseUrl = base.endsWith('/') ? base : base + '/';
  return new URL(src, location.origin + baseUrl).href;
}

export async function importAssets(template) {
  const assetIdsMap = {};
  // add asset
  if (template.assets) {
    await Promise.all(
      Object.values(template.assets).map(async (asset) => {
        const newAsset = await addAsset(asset.url, asset.meta);
        assetIdsMap[asset.id] = newAsset.id;
      })
    );
  }

  const nodes = template.props.nodes;
  Object.values(nodes).map((n) => {
    if (n.src && n.src.startsWith('asset://')) {
      let src = n.src;
      Object.keys(assetIdsMap).map((id) => {
        src = src.replace(id, assetIdsMap[id]);
      });
      n.src = src;
    }
  });
}

export function getAsset(assetUrl, renderContext) {
  assetUrl = assetUrl || '';
  if (assetUrl.startsWith('asset://')) {
    const assetId = assetUrl.split('://')[1];
    const scopeAsset = getLocalAsset(assetId, renderContext);
    if (scopeAsset) return scopeAsset.url;
    const mockAsset = getMockAsset(assetId, renderContext);
    if (mockAsset) {
      return mockAsset.url;
    }
    return `${staticName}/asset/${assetId}`;
  } else {
    const publicPath = renderContext?.publicPath || '';
    const isLocalImage = assetUrl.startsWith('/');
    return isLocalImage ? `${publicPath}${assetUrl}` : assetUrl;
  }
}

function getLocalAsset(id, renderContext) {
  const card = renderContext?.card || {};
  const assets = card?.assets || {};
  const asset = assets[id];
  if (localAssets.has(id)) {
    return localAssets.get(id);
  }
  if (!asset) return null;
  return { ...asset, url: asset.url };
}

function getMockAsset(id, renderContext) {
  const assets = mockList.slice(0);
  const asset = assets.find((asset) => asset.id === id);
  if (!asset) return null;
  return { ...asset, url: renderContext.publicPath + asset.url };
}

async function getImageNodeSrc(context, node) {
  let src = node.src;
  if (!src) return '';
  src = await getAsset(src, context);
  const blob = await fetch(src).then((res) => res.blob());
  const dataUrl = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
  return dataUrl;
}

async function setImageNodeSrc(context, node, src) {
  context.api.updateNode(node.id, { src: src });
}

export async function removeBgWithNode(context, node, { model = '' } = {}) {
  const dataUrl = await getImageNodeSrc(context, node);
  if (!dataUrl) return;
  const data = await request('/v1/image/background', {
    method: 'POST',
    body: JSON.stringify({
      model: model,
      image: dataUrl,
    }),
  });
  const output = data.output;
  const asset = await addAsset(output, {
    filename: 'remove_background.png',
    source: 'rmbg',
  });
  await setImageNodeSrc(context, node, `asset://${asset.id}`);
}

export async function upscaleWithNode(context, node) {
  const dataUrl = await getImageNodeSrc(context, node);
  if (!dataUrl) return;
  const asset = await request('/v1/image/upscale', {
    method: 'POST',
    body: JSON.stringify({
      image: dataUrl,
    }),
  });
  await setImageNodeSrc(context, node, `asset://${asset.id}`);
}

/** 与 index1 FeaturesHero 等一致；空字符串时回退默认 CDN 域名 */
export const staticName = (
  typeof process.env.STATIC_NAME === 'string' && process.env.STATIC_NAME.trim()
    ? process.env.STATIC_NAME.trim()
    : 'https://static.veeso.ai'
).replace(/\/$/, '');

const localAssets = new Map();
export async function addAssetLocal(blob) {
  const id = 'local-asset-' + crypto.randomUUID();
  const asset = {
    id,
    key: id,
    url: URL.createObjectURL(blob),
  };

  localAssets.set(asset.key, asset);
  return asset;
}

/**
 * Retry helper for async functions with exponential backoff.
 * @param {Function} fn - async function which throws on failure
 * @param {number} retries - max retry count
 * @param {number} delay - initial delay in ms
 */
async function retryAsync(fn, retries = 3, delay = 500) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn(attempt);
    } catch (e) {
      lastError = e;
      if (attempt === retries) break;
      await new Promise((res) => setTimeout(res, delay * Math.pow(2, attempt)));
    }
  }
  throw lastError;
}

// 抽象OSS和直传上传实现
async function uploadFileWithProgress({ type, url, blob, onProgress, ossMeta }) {
  if (type === 'oss') {
    const formData = new FormData();
    formData.append('success_action_status', '200');
    formData.append('policy', ossMeta.policy);
    formData.append('x-oss-signature', ossMeta.signature);
    formData.append(
      'x-oss-signature-version',
      ossMeta.x_oss_signature_version || 'OSS4-HMAC-SHA256'
    );
    formData.append('x-oss-credential', ossMeta.x_oss_credential);
    formData.append('x-oss-date', ossMeta.x_oss_date);
    formData.append('key', ossMeta.key);
    if (ossMeta.callback) formData.append('callback', ossMeta.callback);
    if (ossMeta.security_token) formData.append('x-oss-security-token', ossMeta.security_token);
    formData.append('file', blob);

    // 使用XHR上传以获得进度信息
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress({ phase: 'uploading', progress });
        }
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (onProgress) onProgress({ phase: 'uploading', progress: 100 });
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });
      xhr.open('POST', ossMeta.host);
      xhr.send(formData);
    });
  } else {
    // 直传
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress({ phase: 'uploading', progress });
        }
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (onProgress) onProgress({ phase: 'uploading', progress: 100 });
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', blob.type);
      xhr.send(blob);
    });
  }
}

export async function addAsset(dataUrl, meta = {}, onProgress = null) {
  // 1. dataUrl 转为 Blob
  const blob = await fetch(dataUrl).then((res) => res.blob());

  // 2. 处理 SVG 自动命名
  const isSvg = blob.type === 'image/svg+xml';
  if (isSvg && meta) {
    if (!meta.filename) {
      meta.filename = 'import.svg';
    } else {
      if (!meta.filename.endsWith('svg')) {
        meta.filename = meta.filename.replace(/\.[^/.]+$/, '') + '.svg';
      }
    }
  }

  // 3. 获取尺寸
  let dimensions = null;
  try {
    dimensions = await resolveImageDimensions(dataUrl);
  } catch {}

  // 4. 构造上传数据
  const dataURL = await blobToDataURL(blob);
  const uploadData = {
    name: meta.filename,
    parent_id: '',
    size: blob.size,
    source: meta.source || 'import',
    type: meta.type || 'user',
    contentType: blob.type,
    meta: {
      ...meta,
      w: dimensions?.width,
      h: dimensions?.height,
    },
  };

  if (dataURL) {
    uploadData.contentHash = await calculateMd5(dataURL);
  }

  if (onProgress) onProgress({ phase: 'uploading', progress: 0 });

  // 6. 上传接口及远端分发，增加上传异常重试
  let result = await retryAsync(
    async () => {
      return await request('/asset', {
        method: 'POST',
        body: JSON.stringify(uploadData),
      });
    },
    3, // 尝试3次
    800 // 初始等待时间
  );

  // 7. OSS与直传抽象出来，重试策略
  try {
    const { url } = result;
    if (url) {
      await retryAsync(
        async () => {
          if (url.type === 'oss') {
            await uploadFileWithProgress({
              type: 'oss',
              url: url.host,
              blob,
              onProgress,
              ossMeta: url,
            });
          } else {
            await uploadFileWithProgress({
              type: 'direct',
              url,
              blob,
              onProgress,
            });
          }
        },
        2, // 2次重试
        1000
      );
    } else {
      // 如果没有 url 说明asset 已经存在，需要同步 onProgress
      if (onProgress) onProgress({ phase: 'uploading', progress: 100 });
    }
  } catch (e) {
    console.error(e);
    // 6. 上传接口及远端分发，增加上传异常重试
    const { asset } = result;
    await retryAsync(
      async () => {
        return await request(`/asset/${asset.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            dataURL,
          }),
        });
      },
      3, // 尝试 3 次
      800 // 初始等待时间
    );
  }

  // 8. 返回 asset，并修正其 url
  const { asset } = result;
  asset.url = `${staticName}/${asset.key}`;
  return asset;
}

// 计算 content hash (md5) 并添加到 uploadData
// 传入为完整的 data url string，直接对 data 做 md5
async function calculateMd5(data) {
  if (typeof data !== 'string') {
    throw new Error('md5 input should be a data url string');
  }

  // 1. 优先使用 crypto.subtle (浏览器标准)
  if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.digest) {
    try {
      const encoder = new TextEncoder();
      const buffer = encoder.encode(data);
      const hashBuffer = await crypto.subtle.digest('MD5', buffer);
      return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    } catch {
      // fallback
    }
  }

  // 2. 如果没有 crypto，使用 md5.js (MD5)
  try {
    // md5.js 计算字符串
    const hash = md5(data);
    return hash;
  } catch {
    // fallback below if md5.js fails
  }

  return null;
}

/**
 * Resolves image dimensions from a data URL or blob
 * @param {string|Blob} source - The image source (data URL or Blob)
 * @returns {Promise<{width: number, height: number}>} - The image dimensions
 */
export async function resolveImageDimensions(source) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    if (source instanceof Blob) {
      const url = URL.createObjectURL(source);
      img.src = url;
      // Clean up the object URL after the image loads or errors
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
    } else {
      // Assume it's a data URL
      img.src = source;
    }
  });
}

const mockList1 = [
  {
    id: 'placeholder',
    type: 'image',
    meta: {
      filename: 'placeholder',
    },
    url: '/images/image.svg',
    createdAt: 1732703079197,
  },
  {
    id: 'badge1',
    type: 'image',
    meta: {
      filename: 'placeholder',
      width: 190,
      height: 34,
    },
    url: '/images/badges/seede-label.svg',
    createdAt: 1732703079197,
  },
  {
    id: 'badge_en',
    type: 'image',
    meta: {
      filename: 'placeholder',
      width: 217,
      height: 34,
    },
    url: '/images/badges/seede-badge.svg',
    createdAt: 1732703079197,
  },
  {
    id: 'badge2',
    type: 'image',
    meta: {
      filename: 'placeholder',
      width: 206,
      height: 34,
    },
    url: '/images/badges/seede-label2.svg',
    createdAt: 1732703079197,
  },
  {
    id: 'badge_white',
    type: 'image',
    meta: {
      filename: 'badge_white.svg',
      width: 108,
      height: 30,
    },
    url: '/images/badges/seede-white.svg',
    createdAt: 1732703079197,
  },
  {
    id: 'badge_black',
    type: 'image',
    meta: {
      filename: 'badge_black.svg',
      width: 108,
      height: 30,
    },
    url: '/images/badges/seede-black.svg',
    createdAt: 1732703079197,
  },
  {
    id: 'qrcode',
    type: 'image',
    meta: {
      filename: 'qrcode',
    },
    url: '/images/qrcode.svg',
    createdAt: 1732703079197,
  },
  {
    id: '4c0yipiao',
    type: 'image',
    meta: {
      filename: '4c0yipiao4c0yipiao4c0yipiao4c0yipiao',
    },
    url: '/images/scene/1.webp',
    createdAt: 1732703079197,
  },
  {
    id: 'folder',
    type: 'folder',
    meta: {
      filename: 'aa',
    },
    url: '/images/scene/2.webp',
    createdAt: 1732703079197,
  },
  {
    id: '7q08ucwx4',
    type: 'image',
    meta: {
      filename: '4c0yipiao4c0yipiao4c0yipiao4c0yipiao',
    },
    url: '/images/scene/3.webp',
    createdAt: 1732703079197,
  },
  {
    id: 'ue9qu87ik',
    type: 'image',
    meta: {
      filename: '4c0yipiao4c0yipiao4c0yipiao4c0yipiao',
    },
    url: '/images/scene/4.webp',
    createdAt: 1732703079197,
  },
  {
    id: 's4wii6m57',
    type: 'image',
    meta: {
      parentId: 'folder',
    },
    url: '/images/scene/5.webp',
    createdAt: 1732703079197,
  },
  {
    id: '2z4ff4s76',
    type: 'image',
    meta: {
      parentId: 'folder',
    },
    url: '/images/scene/6.webp',
    createdAt: 1732703079197,
  },
  {
    id: '2z4ff4s77',
    type: 'image',
    url: '/images/scene/6.webp',
    createdAt: 1732703079197,
  },
  {
    id: 's4wii6m57',
    type: 'image',
    url: '/images/scene/6.webp',
    createdAt: 1732703079197,
  },
];

const mockList = [...mockList1, ...mockList1, ...mockList1];

export const ASSET_TYPES = {
  folder: 'folder',
  image: 'image',
  file: 'file',
};

export async function getAssets({ skip = 0, limit = 32, source = 'import' } = {}) {
  const user = getUser();
  if (!user) return [];
  if (window.api) {
    return window.api.getAssets();
  } else {
    const assets = await request(`/asset?source=${source}&skip=${skip}&limit=${limit}`);
    return assets.map((asset) => {
      asset.url = `${staticName}/${asset.key}`;
      asset.createdAt = asset.created_at;
      asset.updatedAt = asset.updated_at;
      return asset;
    });
  }
}

export async function getAssetFromAPI(id) {
  const asset = await request(`/asset/${id}`);
  asset.url = `${staticName}/${asset.key}`;
  return asset;
}

export async function imageCompressor(file, { maxSize = 1080 } = {}) {
  const src = await readFileAsBase64(file);
  return new Promise((resolve, reject) => {
    // Create an image element to get dimensions and resize if needed
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      // Check if image needs resizing
      if (img.width > maxSize || img.height > maxSize) {
        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate new dimensions while maintaining aspect ratio
        let newWidth, newHeight;
        if (img.width > img.height) {
          newWidth = maxSize;
          newHeight = Math.round(img.height * (maxSize / img.width));
        } else {
          newHeight = maxSize;
          newWidth = Math.round(img.width * (maxSize / img.height));
        }

        // Set canvas dimensions and draw resized image
        canvas.width = newWidth;
        canvas.height = newHeight;
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Get compressed image data
        resolve({
          filename: file.name,
          loading: true,
          dataURL: canvas.toDataURL('image/webp', 1),
        });
      } else {
        // Use original image if it's already within limits
        resolve({
          filename: file.name,
          loading: true,
          dataURL: src,
        });
      }
    };
    img.src = src;
  });
}

export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
}

export async function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function uploadRefImage(file, onChange, onProgress) {
  if (!file) return;
  const user = getUser();

  const img = await imageCompressor(file, { maxSize: 1920 });

  onChange(img);

  if (user) {
    const asset = await addAsset(
      img.dataURL,
      {
        filename: file.name,
        source: 'ref',
      },
      onProgress
    );
    onChange({
      ...img,
      loading: false,
      url: asset.url,
    });
  } else {
    onChange({
      ...img,
      loading: false,
    });
  }
}
